import React from 'react';
import { ArrowRight, Wifi, Building2, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const OtherBusinessSolutions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center">
              <div>
                <img 
                  src={require('../assets/sikka-logo.jpg')} 
                  alt="Sikka IT Solutions" 
                  className="h-12 w-auto object-contain"
                />
                <p className="text-xs text-slate-600 italic mt-0.5">A name you can trust</p>
              </div>
            </a>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '/'}>
              Back to Home
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Sikka Group of Companies</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Our Other
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> Business Solutions</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              With 30+ years of experience in the IT industry, Sikka Group provides comprehensive solutions 
              across broadband internet services and real estate development.
            </p>
          </div>
        </div>
      </section>

      {/* Sikka Broadband Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <Badge className="mb-4 bg-cyan-100 text-cyan-700">Internet Service Provider</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Sikka Broadband
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Award-winning high-speed internet service provider in Lucknow. Recognized as the Best Internet 
                Service Provider by Radio City, awarded by the Honorable Deputy Chief Minister of Uttar Pradesh.
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <Wifi className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-900">High-Speed Fiber Internet</h3>
                    <p className="text-slate-600">Unlimited data plans from 40 Mbps to 1 Gbps with symmetric upload/download speeds</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Building2 className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Enterprise Leased Line</h3>
                    <p className="text-slate-600">Dedicated 1:1 connectivity for businesses with static IP and unlimited data</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Home className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Additional Services</h3>
                    <p className="text-slate-600">Cable TV, Landline, CCTV, Video Conferencing, VOIP, and Wireless Internet solutions</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => window.open('https://www.sikkanet.com', '_blank')}
                >
                  Visit Sikka Broadband
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-slate-300 hover:bg-slate-50"
                  onClick={() => window.location.href = '/#contact'}
                >
                  Contact Us
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800" 
                alt="Sikka Broadband" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>

          {/* Sikka Broadband Features */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>24/7 Customer Support</CardTitle>
                <CardDescription>Round-the-clock technical assistance and customer care</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  Dedicated support team available via phone (+91 85744 12345) and email (care@sikkanet.com)
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>OTT Apps Included</CardTitle>
                <CardDescription>Premium entertainment with your internet plan</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  Get access to 17-20+ OTT apps including popular streaming platforms with select plans
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Free Wi-Fi ONT</CardTitle>
                <CardDescription>5G Wi-Fi router included</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  Complimentary 5G Wi-Fi ONT with most plans for seamless connectivity across devices
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Sikka Infratech Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 relative">
              <img 
                src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800" 
                alt="Sikka Infratech" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div className="order-1 md:order-2">
              <Badge className="mb-4 bg-purple-100 text-purple-700">Real Estate Development</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Sikka Infratech
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Luxury residential projects that combine modern architecture with natural tranquility. 
                Creating living spaces where dreams take shape and families grow together.
              </p>
              
              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Sikka Riverside Residency</h3>
                <p className="text-slate-600 mb-4">
                  A premium riverside residential project in Lucknow offering 2 BHK apartments to ultra-luxury penthouses 
                  with 29+ lifestyle amenities.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-slate-900">Location</p>
                    <p className="text-slate-600">T.G. New Civil Lines, Lucknow</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">RERA No.</p>
                    <p className="text-slate-600">UPRERAPRJ865353</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Configuration</p>
                    <p className="text-slate-600">2/3/4/5 BHK & Penthouses</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Amenities</p>
                    <p className="text-slate-600">29+ Features</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-slate-700">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                  Riverside location with panoramic Gomti views
                </div>
                <div className="flex items-center text-sm text-slate-700">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                  Party hall, gym, terrace garden, kids play area
                </div>
                <div className="flex items-center text-sm text-slate-700">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                  24x7 security with CCTV surveillance
                </div>
                <div className="flex items-center text-sm text-slate-700">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                  Premium fittings and modern amenities
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => window.open('https://www.sikkainfratech.com', '_blank')}
                >
                  Visit Sikka Infratech
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-slate-300 hover:bg-slate-50"
                  onClick={() => window.location.href = '/#contact'}
                >
                  Get E-Brochure
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Interested in Our Other Services?
          </h2>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Whether you need high-speed internet for your home or business, or looking for your dream residential property, 
            we're here to help.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-blue-600 hover:bg-slate-100"
            onClick={() => window.location.href = '/#contact'}
          >
            Contact Us Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OtherBusinessSolutions;
